import { z } from 'zod';
import {
  CaseStatus,
  DonorType,
  Laterality,
  NO_COORDINATOR_ID,
  NRPOption,
  NRPType,
  OrganType,
  ServiceType,
} from '@/api/apis/case';
import { getPhoneSchema, createDateTimeFields } from '@/utils/helpers';
import { TMDXFile } from '@/api/apis/files';
import { isValidUrl } from './validation.utils';

export const MAX_LINKS = 10;
export const MAX_LINK_DESCRIPTION_LENGTH = 300;

export const linkSchema = z.object({
  id: z.uuid(),
  url: z
    .string()
    .min(1, { message: 'Link should not be empty' })
    .max(MAX_LINK_DESCRIPTION_LENGTH, { message: `Link should be at most ${MAX_LINK_DESCRIPTION_LENGTH} characters` })
    .refine(isValidUrl, { message: 'Incorrect URL format' }),
  description: z
    .string()
    .min(0)
    .max(MAX_LINK_DESCRIPTION_LENGTH, {
      message: `Description should be at most ${MAX_LINK_DESCRIPTION_LENGTH} characters`,
    }),
});

export const linksField = {
  links: z.array(linkSchema).max(MAX_LINKS),
};

export const linksCaseSchema = z.object({
  ...linksField,
});

export const addressSchema = z.object({
  addressId: z.string().optional(),
  latitude: z.number().min(-90).max(90).nullish(),
  longitude: z.number().min(-180).max(180).nullish(),
  city: z.string().optional(),
  state: z.string().optional().nullish(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  timezone: z.string().optional(),
  addressLine: z.string().optional(),
  title: z.string().optional(),
});

export const reallocationFields = {
  reAllocationCase: z.boolean(),
  reAllocationCaseId: z.string().nullish(),
  originalReAllocationCaseId: z.string().nullish(),
  reAllocationCaseTrialProgram: z.any().optional(),
};

export const getLateralityPatch = (
  organType?: OrganType,
  laterality?: Laterality,
): { laterality: Laterality } | Record<string, never> =>
  organType === OrganType.Kidney ? { laterality: laterality ?? Laterality.NotDefined } : {};

export const caseCommonFields = {
  donorId: z.string(),
  donorType: z.enum(DonorType, { error: 'Select Donor Type' }),
  serviceType: z.enum(ServiceType, { error: 'Select resource' }),
  serviceDetails: z
    .string()
    .max(128, { error: `Maximum ${128} characters` })
    .optional()
    .or(z.literal('')),
  organType: z.enum(OrganType, { error: 'Select Organ' }),
  laterality: z.enum(Laterality),
  nrp: z.enum(NRPOption).optional(),
  nrpType: z.enum(NRPType),
  isExpeditedCase: z.boolean(),
  serviceRegionId: z.string(),
  isQuestionaryEditAllowed: z.boolean(),
};

export const donorHospitalDateTimeSchema = z.object(
  createDateTimeFields(
    {
      date: 'donorHospitalDate',
      time: 'donorHospitalTime',
      timezone: 'donorHospitalTimeZone',
    } as const,
    { nullish: true },
  ) as {
    donorHospitalDate: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    donorHospitalTime: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    donorHospitalTimeZone: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
  },
);

export const txCenterORDateTimeSchema = z.object(
  createDateTimeFields(
    {
      date: 'recipientHospitalDate',
      time: 'recipientHospitalTime',
      timezone: 'recipientHospitalTimeZone',
    } as const,
    { nullish: true },
  ) as {
    recipientHospitalDate: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    recipientHospitalTime: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    recipientHospitalTimeZone: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
  },
);

export const caseDonorFields = {
  opoRequest: z.boolean(),
  originalOpoRequest: z.boolean(),
  donorHospitalId: z.string(),
  donorHospitalName: z.string(),
  hospitalCustomLocation: addressSchema.optional(),
  ...donorHospitalDateTimeSchema.shape,
  opoId: z.string(),
  opoName: z.string().optional(),
  opoContactId: z.string().nullish(),
  secondaryOpoContactId: z.string().optional(),
  opoSourceContactPhone: getPhoneSchema({ isRequired: false }),
  secondaryOpoContactPhone: getPhoneSchema({ isRequired: false }),
  reAllocationCaseId: z.string().nullish(),
};

export const caseTxCenterFields = {
  txCenterId: z.string(),
  txCenterName: z.any().optional(),
  txCenterTrialPrograms: z.array(z.any()).optional(),
  txCenterCustomLocation: addressSchema.optional(),
  isServiceWithoutCoordinator: z.boolean().optional(),
  coordinatorId: z.union([z.uuid('Invalid coordinator'), z.literal(''), z.literal(NO_COORDINATOR_ID)]).nullish(),
  receivingSurgeonId: z.union([z.uuid('Invalid surgeon'), z.literal('')]).nullish(),
  ...txCenterORDateTimeSchema.shape,
  recipientHospitalORChangeReason: z.string().max(128).nullish(),
};

export const baseCaseSchema = z.object({
  ...caseCommonFields,
  ...caseDonorFields,
  ...caseTxCenterFields,
  ...reallocationFields,
  ...linksField,
  files: z.array(z.custom<TMDXFile>()).max(50),
  pediatricCase: z.boolean(),
  trialProgram: z.any().optional(),
  status: z.enum(CaseStatus).optional(),
});

export type CaseFormType = z.infer<typeof baseCaseSchema>;
